/**
 * scriptFormat — the script doc type's element model. PURE (no TipTap, no
 * Firebase import), so the keymap logic is unit-testable.
 *
 * Same split as editorCapabilities/slashItems: @tiptap/pm ships untransformed
 * ESM that CRA's jest will not transform, so anything importing TipTap is
 * untestable. The element maps below ARE the feature — Tab/Enter behaviour is
 * ~80% of the felt value of a script editor — so they live on the pure side and
 * ScriptElements.js is left as thin TipTap wiring over them.
 *
 * FORMAT: Dramatists Guild, MUSICAL format (chosen by Anthony 2026-07-24;
 * musicalformat-New.pdf). Layout rules are enforced in CSS — see
 * ScriptElements.css, which carries the measurements — because indentation is
 * presentation; the DOCUMENT stores which KIND of thing each line is.
 *
 * Why semantic nodes rather than styled paragraphs: a paragraph that merely
 * looks centred cannot tell you it is a character cue, so it can't drive the
 * keymap, can't be autocompleted against, and loses its meaning the moment
 * someone edits the styling. These scripts are also a teaching tool for the
 * Triple Threat class — students should see what a real script looks like — so
 * correct format is a feature, not polish.
 */

export const SCRIPT_SCENE_HEADING = 'scriptSceneHeading';
export const SCRIPT_CHARACTER = 'scriptCharacter';
export const SCRIPT_DIALOGUE = 'scriptDialogue';
export const SCRIPT_PARENTHETICAL = 'scriptParenthetical';
export const SCRIPT_LYRIC = 'scriptLyric';
export const SCRIPT_DIRECTION = 'scriptDirection';

/* Catalogue — also drives the "/" menu entries and the format legend, so the
   elements are DISCOVERABLE and not just reachable by a Tab nobody knew about.
   (Nesting shipped built-and-invisible in phase 2; that lesson is applied here.) */
export const SCRIPT_ELEMENTS = [
	{
		name: SCRIPT_SCENE_HEADING,
		label: 'Scene heading',
		hint: 'ACT / SCENE — where we are',
		icon: 'fa-clapperboard',
		keywords: ['scene', 'heading', 'act', 'slug'],
	},
	{
		name: SCRIPT_CHARACTER,
		label: 'Character',
		hint: 'Who speaks next — centred, caps',
		icon: 'fa-user',
		keywords: ['character', 'name', 'who', 'speaker', 'cue'],
	},
	{
		name: SCRIPT_DIALOGUE,
		label: 'Dialogue',
		hint: 'What they say',
		icon: 'fa-comment',
		keywords: ['dialogue', 'dialog', 'speech', 'line', 'says'],
	},
	{
		name: SCRIPT_PARENTHETICAL,
		label: 'Parenthetical',
		hint: 'How they say it — inside a speech',
		icon: 'fa-comment-dots',
		keywords: ['parenthetical', 'paren', 'aside', 'how'],
	},
	{
		name: SCRIPT_LYRIC,
		label: 'Lyric',
		hint: 'Sung text — caps, indented',
		icon: 'fa-music',
		keywords: ['lyric', 'song', 'sing', 'sung', 'verse', 'chorus'],
	},
	{
		name: SCRIPT_DIRECTION,
		label: 'Stage direction',
		hint: 'Action, in parentheses',
		icon: 'fa-person-walking',
		keywords: ['direction', 'stage', 'action', 'business', 'blocking'],
	},
];

export const SCRIPT_NODE_NAMES = SCRIPT_ELEMENTS.map((e) => e.name);

/** Is this node type one of the script elements? */
export function isScriptElement(name) {
	return SCRIPT_NODE_NAMES.includes(name);
}

/* ---- Tab: cycle the element type ---------------------------------------
 *
 * Ordered by authoring frequency, not by the format spec's table order: the
 * character → dialogue pair is the bulk of a script and sits at the front, so
 * the common case is one Tab away. Structural elements (direction, scene
 * heading) are last because you write far fewer of them.
 *
 * The cycle WRAPS, which is what makes it safe to hold Tab: you can never get
 * stuck in a state the keyboard cannot leave.
 */
export const TAB_CYCLE = [
	SCRIPT_CHARACTER,
	SCRIPT_DIALOGUE,
	SCRIPT_PARENTHETICAL,
	SCRIPT_LYRIC,
	SCRIPT_DIRECTION,
	SCRIPT_SCENE_HEADING,
];

/**
 * Next element type for Tab. From anything that is NOT a script element (a
 * paragraph, a heading), Tab enters script mode at the head of the cycle —
 * Character — rather than refusing, so Tab is how you start writing a scene.
 */
export function nextElement(current) {
	const i = TAB_CYCLE.indexOf(current);
	if (i === -1) return TAB_CYCLE[0];
	return TAB_CYCLE[(i + 1) % TAB_CYCLE.length];
}

/** Previous element type, for Shift-Tab. Same entry rule, from the other end. */
export function prevElement(current) {
	const i = TAB_CYCLE.indexOf(current);
	if (i === -1) return TAB_CYCLE[0];
	return TAB_CYCLE[(i - 1 + TAB_CYCLE.length) % TAB_CYCLE.length];
}

/* ---- Enter: advance to what logically follows ---------------------------
 *
 * You never pick the next element from a menu — the document knows what comes
 * after a character cue.
 *
 * LYRIC and DIRECTION repeat themselves on purpose. Lyric line breaks follow
 * musical phrasing, so a song is many short lines in a row; a stage direction
 * often runs to several. Auto-advancing out of either after one line would
 * fight the writer on the most common thing they are doing.
 */
export const ADVANCE_ON_ENTER = {
	[SCRIPT_SCENE_HEADING]: SCRIPT_DIRECTION,
	[SCRIPT_CHARACTER]: SCRIPT_DIALOGUE,
	[SCRIPT_DIALOGUE]: SCRIPT_CHARACTER,
	[SCRIPT_PARENTHETICAL]: SCRIPT_DIALOGUE,
	[SCRIPT_LYRIC]: SCRIPT_LYRIC,
	[SCRIPT_DIRECTION]: SCRIPT_DIRECTION,
};

/** What a fresh block becomes when Enter is pressed at the end of a full one. */
export function advanceFor(current) {
	return ADVANCE_ON_ENTER[current] || null;
}

/* ---- Enter on an EMPTY element: step out --------------------------------
 *
 * The escape hatch, and the counterpart to the repeat rule above: pressing
 * Enter on a blank line steps you OUT one level rather than stacking another
 * empty block. Everything unwinds to Character (the hub of a dialogue medium —
 * a song ends and someone speaks), and a blank Character leaves script mode
 * entirely for an ordinary paragraph.
 *
 * So: blank Enter, blank Enter = out. The same two-press idiom the container
 * blocks already use (see BlockExits) — every block in this editor has to have
 * a way out that the keyboard alone can find.
 */
export const ESCAPE_ON_EMPTY = {
	[SCRIPT_SCENE_HEADING]: SCRIPT_CHARACTER,
	[SCRIPT_CHARACTER]: 'paragraph',
	[SCRIPT_DIALOGUE]: SCRIPT_CHARACTER,
	[SCRIPT_PARENTHETICAL]: SCRIPT_CHARACTER,
	[SCRIPT_LYRIC]: SCRIPT_CHARACTER,
	[SCRIPT_DIRECTION]: SCRIPT_CHARACTER,
};

/** What an EMPTY block converts to when Enter is pressed in it. */
export function escapeFor(current) {
	return ESCAPE_ON_EMPTY[current] || null;
}

/* ---- The whole Enter decision, as one pure function ---------------------
 *
 * Every rule Return obeys, in one place, so the keymap is a translation rather
 * than a second implementation. Actions:
 *   advance — finish this line, open the element that follows
 *   stanza  — leave a blank line and stay in lyric
 *   escape  — convert this empty block to `target`
 *   default — hand Return back to ProseMirror (an ordinary split)
 *
 * The stanza case is required by the format, not a nicety: lyric stanzas are
 * SEPARATED BY A BLANK LINE, so if the first blank line escaped out of the song
 * there would be no way to author a stanza break at all. Instead the first
 * blank leaves the gap, and a SECOND consecutive blank means the song is over —
 * the same "blank, blank, out" idiom every other element already follows.
 */
export function enterAction({ type, isEmpty, atEnd, prevIsBlankLyric = false }) {
	if (!isScriptElement(type)) return { action: 'default', target: null };

	if (isEmpty) {
		if (type === SCRIPT_LYRIC && !prevIsBlankLyric) {
			return { action: 'stanza', target: SCRIPT_LYRIC };
		}
		return { action: 'escape', target: escapeFor(type) };
	}

	// Mid-line Return is an ordinary split — both halves keep the type. Auto-
	// advance is for finishing a line; retyping the tail of a speech as a
	// character cue would be wrong.
	if (!atEnd) return { action: 'default', target: null };

	return { action: 'advance', target: advanceFor(type) };
}

/* ---- Character extraction ----------------------------------------------- */

/** Concatenated plain text of a node's inline content. */
function inlineTextOf(node) {
	const out = [];
	const walk = (n) => {
		if (!n || typeof n !== 'object') return;
		if (n.type === 'text' && typeof n.text === 'string') out.push(n.text);
		(n.content || []).forEach(walk);
	};
	(node.content || []).forEach(walk);
	return out.join('');
}

/**
 * Normalize a character cue into the name we autocomplete against.
 *
 * Cues carry extensions in performance scripts — MARGO (CONT'D), MARGO (V.O.) —
 * and those are the same character. Stripping a TRAILING parenthetical keeps
 * the list one row per person instead of one row per variation.
 */
export function normalizeCharacterName(raw) {
	return String(raw || '')
		.replace(/\([^)]*\)\s*$/, '')
		.replace(/\s+/g, ' ')
		.trim()
		.toUpperCase();
}

/**
 * Every character named in this document, most-used first.
 *
 * Frequency order rather than first-appearance: in a full script the leads are
 * the names you retype constantly, and they should be the ones a single
 * keystroke reaches. Ties keep first-appearance order so the list is stable
 * while typing rather than reshuffling under the cursor.
 *
 * Scoped to THIS document by design — a character list is a property of the
 * script, not of the studio.
 */
export function charactersFromNames(rawNames) {
	const seen = new Map(); // name -> { name, count, firstAt }
	let order = 0;
	(rawNames || []).forEach((raw) => {
		const name = normalizeCharacterName(raw);
		if (!name) return;
		const hit = seen.get(name);
		if (hit) hit.count += 1;
		else seen.set(name, { name, count: 1, firstAt: order++ });
	});
	return [...seen.values()].sort((a, b) => b.count - a.count || a.firstAt - b.firstAt);
}

/**
 * Characters named in a document, from its TipTap JSON.
 *
 * Split from charactersFromNames so the live editor can collect cues by walking
 * the ProseMirror document directly — serializing the whole script to JSON on
 * every keystroke of an autocomplete would be wasteful — while both paths share
 * one ordering implementation and cannot drift.
 */
export function extractCharacters(docJson) {
	const names = [];
	const walk = (node) => {
		if (!node || typeof node !== 'object') return;
		if (node.type === SCRIPT_CHARACTER) names.push(inlineTextOf(node));
		(node.content || []).forEach(walk);
	};
	walk(docJson);
	return charactersFromNames(names);
}

/**
 * Rank the character list against what has been typed so far.
 *
 * Same exactness-first ranking as the "/" menu (slashItems.filterSlashItems),
 * for the same reason: typing a name that is a prefix of a longer one must not
 * put the longer one first. An empty query returns everyone — pressing Enter
 * after a speech opens a blank cue, and seeing the cast at that moment is the
 * point of the feature.
 */
export function filterCharacters(characters, query) {
	const q = normalizeCharacterName(query);
	if (!q) return characters;
	const score = (c) => {
		if (c.name === q) return 0;
		if (c.name.startsWith(q)) return 1;
		if (c.name.includes(q)) return 2;
		return Infinity;
	};
	return characters
		.map((c, i) => ({ c, s: score(c), i }))
		.filter((r) => r.s !== Infinity)
		.sort((a, b) => a.s - b.s || a.i - b.i)
		.map((r) => r.c);
}

/**
 * Does this document contain script nodes?
 *
 * Load-bearing safety check, not a convenience. Capabilities gate EXTENSIONS,
 * so if a doc holding script nodes is opened WITHOUT the script capability,
 * ProseMirror does not error — it silently drops the unknown nodes, and the
 * autosave that follows writes the stripped document back over the original.
 * The editor therefore turns the capability on for any content that contains
 * script nodes, whatever the stored `type` says, so a doc whose type field is
 * missing or wrong cannot be quietly destroyed by opening it.
 */
export function containsScriptNodes(docJson) {
	let found = false;
	const walk = (node) => {
		if (found || !node || typeof node !== 'object') return;
		if (isScriptElement(node.type)) {
			found = true;
			return;
		}
		(node.content || []).forEach(walk);
	};
	walk(docJson);
	return found;
}
